/**
 * Fishing bobber (vanilla FishingHook), DOM-free. One per player:
 *
 *   const hooks = new BobberSim();
 *   hooks.cast(ctx, x, y, z, yaw, pitch, { lure, luck })   // right-click with a rod
 *   hooks.tick(ctx)                                        // 20 Hz
 *   const r = hooks.reel(ctx)                              // right-click again -> { outcome, damage }
 *
 * Flight: velocity = dir * (0.6/|dir| + 0.5 +- noise) with dir = (-sin yaw, tan pitch, -cos yaw), gravity 0.03,
 * drag 0.92; it stops where it lands. Entering water it slows to (0.3, 0.2, 0.3) and bobs: a spring pulls it to
 * the surface (y - cell - waterHeight), x/z drag 0.9. While bobbing in water `FishTimer` (game/fishing.ts) runs
 * wait -> trail (wake particles close in) -> bite (the bobber dips: vy = -0.4 * [0.6, 1]). Reeling during the
 * bite rolls `rollCatch` (treasure only in open water: a 5x5 pool, one block deep, with open air above), flings
 * the stack to the player with vanilla arc velocity (0.1 * delta, + 0.08 * delta-length^0.25 up) and drops a 1-6 XP
 * orb at the player. Flying into a mob hooks it (0-damage hit, bobber follows it; reeling pulls it by 0.1 * delta);
 * an item entity is hooked and pulled the same way. The hook is removed when the player dies, leaves the rod, or is
 * more than 32 blocks away. Durability per `rodDamage`.
 */

import { collisionOf, IS_WATERY, LILY_PAD_ID, NO_BOXES, WATER_AMOUNT, IS_WATER_SOURCE } from '../blocks';
import { WORLD_HEIGHT } from '../constants';
import { FishTimer, rodDamage, rollCatch, rollXp, type ReelOutcome, type Rnd } from '../game/fishing';
import { I } from '../game/itemIds';
import type { Stack } from '../game/types';
import { boxHits, clipMove, type ShapesAt } from '../world/collide';
import type { Mob } from './mob';

/** Anything the line can drag (a Mob or an ItemEntity). */
export interface Pullable { x: number; y: number; z: number; vx: number; vy: number; vz: number }

export type BobberState = 'flying' | 'bobbing' | 'hooked';
export type BobberFx = 'cast' | 'splash' | 'wake' | 'bite' | 'escaped' | 'reel' | 'catch';

export interface Hooked { target: Pullable; kind: 'mob' | 'item'; yOff: number; gone(): boolean }

export interface Bobber {
  x: number; y: number; z: number;
  px: number; py: number; pz: number;
  vx: number; vy: number; vz: number;
  state: BobberState;
  onGround: boolean;
  /** Ticks resting on the ground (removed after 1200). */
  life: number;
  age: number;
  lure: number; luck: number;
  timer: FishTimer | null;
  /** Direction (radians) the fish swims in from during the trail phase. */
  fishAngle: number;
  /** True during the bite phase: reel now. */
  biting: boolean;
  openWater: boolean;
  hooked: Hooked | null;
}

export interface BobberCtx {
  world: { getBlock(x: number, y: number, z: number): number };
  rnd?: Rnd;
  /** Feet position; the hook is dropped when the player is dead. */
  player(): { x: number; y: number; z: number; eyeHeight?: number; alive: boolean };
  holdingRod(): boolean;
  raining(x: number, y: number, z: number): boolean;
  /** True when nothing solid is above (x, y, z) up to the world top. */
  skyVisible(x: number, y: number, z: number): boolean;
  raycastMob(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, maxDist: number): { mob: Mob; dist: number } | null;
  /** 0-damage hit with knockback away from (fromX, fromZ). */
  hitMob(mob: Mob, fromX: number, fromZ: number): void;
  /** A free item entity whose box contains the point (inflated by r). */
  itemAt?(x: number, y: number, z: number, r: number): { target: Pullable; gone(): boolean } | null;
  spawnItem(stack: Stack, x: number, y: number, z: number, vx: number, vy: number, vz: number): void;
  spawnXp(amount: number, x: number, y: number, z: number): void;
  fx?(kind: BobberFx, x: number, y: number, z: number): void;
  loaded?(x: number, z: number): boolean;
}

export interface ReelResult { outcome: ReelOutcome; damage: number; stack?: Stack; xp?: number }

export const MAX_RANGE = 32;
const HALF = 0.125;
const SIZE = 0.25;
const EPS = 1e-4;
const GRAVITY = 0.03;
const MAX_REST = 1200;

/** Fish / junk only: a fishing spot that is not open water cannot give treasure. */
function isTreasure(s: Stack): boolean {
  return s.id === I.BOW || s.id === I.ENCHANTED_BOOK || s.id === I.NAME_TAG || s.id === I.NAUTILUS_SHELL || s.id === I.SADDLE
    || (s.id === I.FISHING_ROD && !!s.ench?.length);
}

export class BobberSim {
  hook: Bobber | null = null;
  private readonly box = [0, 0, 0, 0, 0, 0];

  constructor(private readonly rndDefault: Rnd = Math.random) {}

  /** Throw a new bobber (replaces an existing one). Position = eye - 0.3 along the facing, like vanilla. */
  cast(ctx: BobberCtx, eyeX: number, eyeY: number, eyeZ: number, yaw: number, pitch: number, opts: { lure?: number; luck?: number } = {}): Bobber {
    const rnd = ctx.rnd ?? this.rndDefault;
    const fx = -Math.sin(yaw), fz = -Math.cos(yaw);
    const fy = Math.max(-5, Math.min(5, Math.tan(pitch)));
    const d = Math.hypot(fx, fy, fz) || 1;
    const tri = (): number => 0.5 + (rnd() - rnd()) * 0.0103365;
    const k = 0.6 / d;
    const b: Bobber = {
      x: eyeX + fx * 0.3, y: eyeY, z: eyeZ + fz * 0.3, px: 0, py: 0, pz: 0,
      vx: fx * (k + tri()), vy: fy * (k + tri()), vz: fz * (k + tri()),
      state: 'flying', onGround: false, life: 0, age: 0, lure: opts.lure ?? 0, luck: opts.luck ?? 0,
      timer: null, fishAngle: 0, biting: false, openWater: false, hooked: null,
    };
    b.px = b.x; b.py = b.y; b.pz = b.z;
    this.hook = b;
    ctx.fx?.('cast', b.x, b.y, b.z);
    return b;
  }

  clear(): void { this.hook = null; }

  /** Vanilla fluid height of the water at the cell (0 = none): sources 8/9, flowing amount/9, 1 under more water. */
  waterHeight(ctx: BobberCtx, bx: number, by: number, bz: number): number {
    if (by < 0 || by >= WORLD_HEIGHT) return 0;
    const id = ctx.world.getBlock(bx, by, bz);
    if (IS_WATERY[id] !== 1) return 0;
    if (by + 1 < WORLD_HEIGHT && IS_WATERY[ctx.world.getBlock(bx, by + 1, bz)] === 1) return 1;
    return WATER_AMOUNT[id] / 9;
  }

  private shapes(ctx: BobberCtx): ShapesAt {
    return (x, y, z) => (y < 0 || y >= WORLD_HEIGHT ? NO_BOXES : collisionOf(ctx.world.getBlock(x, y, z)));
  }

  /** Vanilla calculateOpenWater: source water layers below, air (or lily pads) above, in a 5x5 around the hook. */
  openWaterAt(ctx: BobberCtx, bx: number, by: number, bz: number): boolean {
    let inside = 0, above = 0, sawAbove = false;
    for (let dy = -1; dy <= 2; dy++) {
      let layer: 'water' | 'air' | null = null;
      for (let dx = -2; dx <= 2; dx++) for (let dz = -2; dz <= 2; dz++) {
        const y = by + dy;
        const id = y < 0 || y >= WORLD_HEIGHT ? 0 : ctx.world.getBlock(bx + dx, y, bz + dz);
        const t = id === 0 || id === LILY_PAD_ID ? 'air' : IS_WATER_SOURCE[id] === 1 && collisionOf(id).length === 0 ? 'water' : null;
        if (t === null || (layer !== null && layer !== t)) return false;
        layer = t;
      }
      if (layer === 'water') { if (sawAbove) return false; inside++; } else { sawAbove = true; above++; }
    }
    return inside > 0 && above > 0;
  }

  tick(ctx: BobberCtx): void {
    const b = this.hook;
    if (!b) return;
    const rnd = ctx.rnd ?? this.rndDefault;
    const p = ctx.player();
    const eye = p.y + (p.eyeHeight ?? 1.52);
    if (!p.alive || !ctx.holdingRod() || (p.x - b.x) ** 2 + (eye - b.y) ** 2 + (p.z - b.z) ** 2 > MAX_RANGE * MAX_RANGE) { this.hook = null; return; }
    b.px = b.x; b.py = b.y; b.pz = b.z;
    if (ctx.loaded && !ctx.loaded(b.x, b.z)) return;
    b.age++;
    if (b.onGround) { if (++b.life >= MAX_REST) { this.hook = null; return; } } else b.life = 0;

    const bx = Math.floor(b.x), by = Math.floor(b.y), bz = Math.floor(b.z);
    const w = this.waterHeight(ctx, bx, by, bz);

    if (b.state === 'hooked') {
      const h = b.hooked;
      if (!h || h.gone()) { b.hooked = null; b.state = 'flying'; b.vx = b.vy = b.vz = 0; }
      else { b.x = h.target.x; b.y = h.target.y + h.yOff; b.z = h.target.z; b.vx = b.vy = b.vz = 0; return; }
    }
    if (b.state === 'flying') {
      if (w > 0) {
        b.vx *= 0.3; b.vy *= 0.2; b.vz *= 0.3;
        b.state = 'bobbing';
        b.timer = new FishTimer(rnd, { lure: b.lure, rain: ctx.raining(bx, by + 1, bz), sky: ctx.skyVisible(bx, by + 1, bz) });
        ctx.fx?.('splash', b.x, by + w, b.z);
        return;
      }
      if (this.collideEntities(ctx, b)) return;
    } else if (b.state === 'bobbing') {
      // popped just above the surface (a bite's rebound): stay bobbing while water is right below
      if (w <= 0 && this.waterHeight(ctx, bx, by - 1, bz) <= 0) { b.state = 'flying'; b.timer = null; b.biting = false; }
      else {
        const wv = w > 0 ? w : -1 + this.waterHeight(ctx, bx, by - 1, bz); // above the cell: aim for the water below
        let d0 = b.y + b.vy - by - wv;
        if (Math.abs(d0) < 0.01) d0 += Math.sign(d0) * 0.1;
        b.vx *= 0.9; b.vz *= 0.9;
        b.vy -= d0 * rnd() * 0.2;
        if (w > 0) this.fishing(ctx, b, bx, by, bz, w, rnd);
      }
    }
    if (w <= 0) b.vy -= GRAVITY;

    const bumped = this.move(ctx, b);
    if (b.state === 'flying' && (b.onGround || bumped)) { b.vx = b.vy = b.vz = 0; }
    b.vx *= 0.92; b.vy *= 0.92; b.vz *= 0.92;
  }

  /** Mob / item hit while flying; true when the hook latched on. */
  private collideEntities(ctx: BobberCtx, b: Bobber): boolean {
    const len = Math.hypot(b.vx, b.vy, b.vz);
    if (len < 1e-6) return false;
    const hit = ctx.raycastMob(b.x, b.y, b.z, b.vx, b.vy, b.vz, len);
    if (hit) {
      const m = hit.mob;
      const t = hit.dist / len;
      b.x += b.vx * t; b.y += b.vy * t; b.z += b.vz * t;
      ctx.hitMob(m, b.x - b.vx, b.z - b.vz);
      b.hooked = { target: m, kind: 'mob', yOff: m.height * 0.8, gone: () => !m.alive };
      b.state = 'hooked'; b.vx = b.vy = b.vz = 0;
      return true;
    }
    let it: ReturnType<NonNullable<BobberCtx['itemAt']>> = null;
    for (const t of [0.25, 0.5, 0.75, 1]) { it = ctx.itemAt?.(b.x + b.vx * t, b.y + b.vy * t, b.z + b.vz * t, HALF + 0.1) ?? null; if (it) break; }
    if (it) {
      b.hooked = { target: it.target, kind: 'item', yOff: 0.125, gone: it.gone };
      b.state = 'hooked'; b.vx = b.vy = b.vz = 0;
      return true;
    }
    return false;
  }

  private fishing(ctx: BobberCtx, b: Bobber, bx: number, by: number, bz: number, w: number, rnd: Rnd): void {
    const t = b.timer!;
    t.ctx.rain = ctx.raining(bx, by + 1, bz);
    t.ctx.sky = ctx.skyVisible(bx, by + 1, bz);
    if (t.state === 'wait' && t.ticks === 1) b.openWater = this.openWaterAt(ctx, bx, by, bz);
    const surf = by + w;
    const ev = t.tick();
    if (ev === 'trail') b.fishAngle = rnd() * Math.PI * 2;
    else if (ev === 'bite') {
      b.biting = true;
      b.vy = -0.4 * (0.6 + rnd() * 0.4);
      ctx.fx?.('bite', b.x, surf, b.z);
    } else if (ev === 'escaped') { b.biting = false; ctx.fx?.('escaped', b.x, surf, b.z); }
    if (t.state === 'trail' && rnd() < 0.5) {
      // the fish closes in: the wake is `ticks * 0.05` blocks out along its heading and shrinks to the bobber
      const r = t.ticks * 0.05;
      ctx.fx?.('wake', b.x + Math.sin(b.fishAngle) * r, surf, b.z + Math.cos(b.fishAngle) * r);
    }
  }

  /** Entity.move for the 0.25 box: y first, then the longer horizontal axis. True on a horizontal collision. */
  private move(ctx: BobberCtx, b: Bobber): boolean {
    const box = this.box, shapes = this.shapes(ctx);
    box[0] = b.x - HALF; box[1] = b.y; box[2] = b.z - HALF; box[3] = b.x + HALF; box[4] = b.y + SIZE; box[5] = b.z + HALF;
    if (boxHits(box, EPS, shapes)) { b.y += 0.1; b.vx = b.vz = 0; b.vy = 0; return false; }
    let hit = false;
    const dy = b.vy;
    const cy = clipMove(box, 1, dy, EPS, shapes);
    box[1] += cy; box[4] += cy;
    b.onGround = dy < 0 && cy > dy;
    if (cy !== dy) b.vy = 0;
    const order = Math.abs(b.vx) >= Math.abs(b.vz) ? [0, 2] : [2, 0];
    for (const axis of order) {
      const want = axis === 0 ? b.vx : b.vz;
      const c = clipMove(box, axis, want, EPS, shapes);
      box[axis] += c; box[axis + 3] += c;
      if (c !== want) { hit = true; if (axis === 0) b.vx = 0; else b.vz = 0; }
    }
    b.x = (box[0] + box[3]) / 2; b.y = box[1]; b.z = (box[2] + box[5]) / 2;
    return hit;
  }

  /** Right-click while a hook is out: pulls in whatever it holds, removes the hook. `damage` is the rod wear to apply. */
  reel(ctx: BobberCtx): ReelResult {
    const b = this.hook;
    if (!b) return { outcome: 'nothing', damage: 0 };
    const rnd = ctx.rnd ?? this.rndDefault;
    const p = ctx.player();
    const px = p.x, py = p.y + (p.eyeHeight ?? 1.52) - 0.4, pz = p.z;
    this.hook = null;
    ctx.fx?.('reel', b.x, b.y, b.z);
    if (b.hooked && !b.hooked.gone()) {
      const t = b.hooked.target;
      t.vx += (px - b.x) * 0.1; t.vy += (py - b.y) * 0.1; t.vz += (pz - b.z) * 0.1;
      const o: ReelOutcome = b.hooked.kind === 'item' ? 'item' : 'mob';
      return { outcome: o, damage: rodDamage(o) };
    }
    if (b.state === 'bobbing' && b.biting) {
      let stack = rollCatch(rnd, b.luck);
      for (let i = 0; i < 12 && !b.openWater && isTreasure(stack); i++) stack = rollCatch(rnd, b.luck);
      if (!b.openWater && isTreasure(stack)) stack = rollCatch(rnd, 0);
      const dx = px - b.x, dy = py - b.y, dz = pz - b.z;
      ctx.spawnItem(stack, b.x, b.y, b.z, dx * 0.1, dy * 0.1 + Math.sqrt(Math.sqrt(dx * dx + dy * dy + dz * dz)) * 0.08, dz * 0.1);
      const xp = rollXp(rnd);
      ctx.spawnXp(xp, p.x, p.y + 0.5, p.z + 0.5);
      ctx.fx?.('catch', b.x, b.y, b.z);
      return { outcome: 'catch', damage: rodDamage('catch'), stack, xp };
    }
    if (b.onGround) return { outcome: 'block', damage: rodDamage('block') };
    return { outcome: 'nothing', damage: 0 };
  }
}
